import { useMemo, useState, useEffect } from 'react';
import { Alert, Button, Card, Select, Tag, Typography, message } from 'antd';
import { DesktopOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { TenantOrder } from '@/mocks/orders';
import { formatVND, toNumber } from '@/utils/format';
import qrcode from 'qrcode';
import { OrderState, resolveCollectTotal } from '@tcr-erp/shared';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { bankService } from '@/services/bank.service';
import { ROUTES } from '@/constants/routes';
import {
  buildOrderQrPayload,
  resolveAccountBankBin,
  selectOrderBankAccounts,
} from '../utils/orderBankAccount';

const { Text } = Typography;

interface OrderQrPaymentProps {
  order: TenantOrder;
}

export function OrderQrPayment({ order }: OrderQrPaymentProps) {
  const [qrUrl, setQrUrl] = useState<string>('');
  const [pickedAccountId, setPickedAccountId] = useState<string | null>(null);

  // TCR-1901: trước fix, tên ngân hàng và số tài khoản bị hardcode ngay trong
  // file này nên mọi đơn của mọi chi nhánh đều ra đúng một mã quét. Giờ đọc từ
  // thiết lập phương thức thanh toán và lọc theo chi nhánh của đơn.
  const bankAccountsQuery = useBankAccounts();
  const banksQuery = useQuery({
    queryKey: ['bank-list'],
    queryFn: () => bankService.list(),
    staleTime: 300_000,
  });

  const accounts = useMemo(
    () => selectOrderBankAccounts(bankAccountsQuery.data, order.store_id),
    [bankAccountsQuery.data, order.store_id],
  );
  // Danh sách đổi theo chi nhánh/đơn nên lựa chọn cũ có thể không còn hợp lệ —
  // luôn rơi về ứng viên đầu tiên thay vì giữ một id đã biến mất.
  const account =
    accounts.find((a) => a._id === pickedAccountId) ?? accounts[0] ?? null;

  const bankName = account?.bank_name ?? '';
  const accountNumber = account?.account_number ?? '';
  const isStoreScoped = account?.store_id != null;

  // Tiền đã thu lấy ở `paid_amount` (BE giữ), KHÔNG cộng lại từ danh sách phiếu thu: thu tại
  // quầy POS không sinh phiếu thu kế toán, cộng theo phiếu sẽ ra thiếu và QR đòi thừa tiền.
  const totalDeposit = toNumber(order.paid_amount);

  // TCR-1077: đơn thanh toán hỗn hợp CK+TM khai riêng phần chuyển khoản ở
  // `non_cash_amount`. QR chỉ được quét đúng phần CK đã nhập — KHÔNG lấy cả tổng
  // đơn (phần tiền mặt khách trả trực tiếp, không quét QR). Đơn 100% CK không set
  // `non_cash_amount` (null) → giữ hành vi cũ: quét phần còn phải thu của cả đơn.
  const nonCashAmount = order.non_cash_amount;
  const hasNonCashPortion = nonCashAmount != null && Number(nonCashAmount) > 0;
  // TCR-2054: "còn lại" tính trên TỔNG KHÁCH PHẢI TRẢ (gồm ship người mua trả), không phải
  // `total_amount` (= summary.total, không gồm ship) — nếu không QR đòi thiếu đúng phí ship.
  const collectTotal = resolveCollectTotal(order);
  const amountToPay = hasNonCashPortion
    ? Number(nonCashAmount)
    : Math.max(0, collectTotal - totalDeposit);
  const isPaid = order.state === OrderState.PAID || order.payment_state === 'PAID';
  // Với đơn hỗn hợp, "đã thu đủ" xét theo phần CK; đơn thường xét theo tổng đơn.
  const settledCeiling = hasNonCashPortion ? Number(nonCashAmount) : collectTotal;
  const isQrDisabled = amountToPay <= 0 || totalDeposit >= settledCeiling || isPaid;

  const content = `THANH TOAN DON HANG ${order.order_number}`;

  // BIN Napas của ngân hàng đang chọn. Không tra được thì KHÔNG vẽ QR: thà để
  // khách chuyển khoản tay còn hơn đưa mã trỏ nhầm ngân hàng nhận tiền.
  const bankBin = useMemo(
    () => (account ? resolveAccountBankBin(account, banksQuery.data ?? []) : null),
    [account, banksQuery.data],
  );

  const qrPayload = useMemo(() => {
    if (!account || !bankBin) return null;
    return buildOrderQrPayload({
      bin: bankBin,
      accountNumber: account.account_number,
      amount: amountToPay,
      content,
    });
  }, [account, bankBin, amountToPay, content]);

  useEffect(() => {
    if (!qrPayload) {
      setQrUrl('');
      return;
    }
    // Sử dụng thư viện qrcode để sinh DataURL thật
    qrcode.toDataURL(qrPayload, { width: 180, margin: 1 })
      .then(url => {
        setQrUrl(url);
      })
      .catch(err => {
        console.error('Lỗi sinh mã QR:', err);
      });
  }, [qrPayload]);

  const handlePushToSecondaryScreen = () => {
    message.success('Đã đẩy mã QR thanh toán sang màn hình phụ của khách hàng.');
  };

  const isLoadingAccounts = bankAccountsQuery.isLoading;
  const hasNoAccount = !isLoadingAccounts && !account;

  // TCR-2054: đã thanh toán đủ thì không còn gì để quét — ẩn hẳn thay vì hiện mã mờ.
  if (order.payment_state === 'PAID') return null;

  return (
    <Card size="small" style={{ maxWidth: 360, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, textTransform: 'uppercase', color: '#555' }}>
        Thanh toán chuyển khoản
      </div>

      {hasNoAccount ? (
        <Alert
          type="warning"
          showIcon
          style={{ textAlign: 'left' }}
          message="Chưa có tài khoản nhận chuyển khoản"
          description={
            <span>
              Chi nhánh này chưa thiết lập ngân hàng nhận tiền. Thêm tài khoản tại{' '}
              <Link to={ROUTES.finance.paymentMethods}>Phương thức thanh toán</Link>.
            </span>
          }
        />
      ) : (
        <>
          {accounts.length > 1 && (
            <div style={{ marginBottom: 12, textAlign: 'left' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Ngân hàng nhận tiền</Text>
              <Select
                aria-label="Ngân hàng nhận tiền"
                value={account?._id}
                onChange={setPickedAccountId}
                style={{ width: '100%', marginTop: 4 }}
                options={accounts.map((a) => ({
                  value: a._id,
                  label: `${a.bank_name} — ${a.account_number}`,
                }))}
              />
            </div>
          )}

          <div style={{ background: '#f9f9f9', padding: 12, borderRadius: 8, display: 'inline-block', marginBottom: 12 }}>
            <div style={{ marginBottom: 8, color: '#a4193d', fontWeight: 'bold', fontSize: 16 }}>
              Số tiền: {formatVND(amountToPay)}
            </div>
            {qrUrl ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={qrUrl}
                  alt="Mã QR Thanh toán"
                  style={{
                    width: 160,
                    height: 160,
                    display: 'block',
                    margin: '0 auto',
                    filter: isQrDisabled ? 'blur(4px) grayscale(100%)' : 'none',
                    opacity: isQrDisabled ? 0.3 : 1
                  }}
                />
                {isQrDisabled && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#a4193d',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    border: '2px solid #a4193d',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    padding: '4px',
                    textAlign: 'center'
                  }}>
                    KHÔNG KHẢ DỤNG
                  </div>
                )}
              </div>
            ) : (
              <div style={{ width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#eee', fontSize: 12, padding: 8 }}>
                {account && !bankBin
                  ? `Chưa hỗ trợ sinh mã QR cho ${bankName}. Vui lòng chuyển khoản theo thông tin bên dưới.`
                  : 'Đang sinh QR...'}
              </div>
            )}
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 2 }}>
            {bankName}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 1, color: '#0958d9', marginBottom: 8 }}>
            {accountNumber}
          </div>
          <div style={{ marginBottom: 8 }}>
            <Tag color={isStoreScoped ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
              {isStoreScoped
                ? `Tài khoản riêng của chi nhánh${order.store_name ? `: ${order.store_name}` : ''}`
                : 'Tài khoản dùng chung mọi chi nhánh'}
            </Tag>
          </div>

          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Số tiền còn lại: </Text>
            <strong style={{ fontSize: 16, color: '#a4193d' }}>{formatVND(amountToPay)}</strong>
          </div>

          <div style={{ fontSize: 12, background: '#f5f5f5', padding: '6px 12px', borderRadius: 4, display: 'inline-block', marginBottom: 16, fontFamily: 'monospace' }}>
            <span style={{ color: '#666' }}>Nội dung: </span>
            <strong>{content}</strong>
          </div>

          <div>
            <Button
              type="primary"
              icon={<DesktopOutlined />}
              onClick={handlePushToSecondaryScreen}
              disabled={isQrDisabled || !qrUrl}
              style={{ width: '100%', backgroundColor: isQrDisabled ? undefined : '#000', borderColor: isQrDisabled ? undefined : '#000' }}
            >
              Hiển thị QR lên màn phụ
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
